import React, { useEffect } from 'react';

import { getUserStripeCharges } from '../../services/UsersService';
import { useFetchData } from '../../infrastructure/hooks/useFetchData';
import { StripeCharge, StripeRefund } from '../../types/Stripe';
import { ApiStatus } from '../../infrastructure/constants/apiStatus';
import { timestampToDateString } from '../../infrastructure/utils/common';
import { stripeChargeUrl } from '../../libs/stripeLib';

import StripeChargesTable from './StripeChargesTable/StripeChargesTable';
import {
  StripeChargeRowProps,
  StripeRefundRowProps,
} from './StripeChargesTable/StripeChargesRow';

type Props = {
  userId: string;
  stripeCustomerId: string;
};

const UserStripeCharges = ({ userId, stripeCustomerId }: Props) => {
  const [apiStatus, charges, fetchCharges] = useFetchData<StripeCharge[]>();

  useEffect(() => {
    fetchCharges(() => getUserStripeCharges(userId, stripeCustomerId));
  }, [userId, stripeCustomerId, fetchCharges]);

  if (apiStatus === ApiStatus.loading)
    return <div>Loading Stripe charges...</div>;

  if (apiStatus === ApiStatus.error)
    return <div>An error occurred while loading the stripe charges.</div>;

  if (!charges || charges.length === 0)
    return (
      <div>There are no charges associated with this Stripe customer.</div>
    );

  const convertStripeRefund = (refund: StripeRefund): StripeRefundRowProps => ({
    id: refund.id,
    amount: refund.amount / 100,
    status: refund.status,
    createdAt: timestampToDateString(refund.created * 1000),
  });
  const convertStripeCharge = (charge: StripeCharge): StripeChargeRowProps => {
    const refunds = (charge.refunds?.data || []).filter(
      refund => refund.status !== 'canceled'
    );

    return {
      id: charge.id,
      amount: charge.amount / 100,
      refundAmount:
        refunds.map(r => r.amount).reduce((total, next) => total + next, 0) /
        100,
      status: charge.status,
      createdAt: timestampToDateString(charge.created * 1000),
      stripeLink: stripeChargeUrl(charge.id),
      refunds: refunds.map(convertStripeRefund),
      // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
      // @ts-ignore
      actions: [],
    };
  };

  return <StripeChargesTable charges={charges.map(convertStripeCharge)} />;
};

export default UserStripeCharges;
